import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import lang from '../../lib/language';
import { PurchasePaymentTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { updateAutoPass } from '../../lib/store/crm';
import { getCurrentAccountTradingPlan, getCurrentDetailedAccount } from '../../lib/store/crm/helpers';
import { CRMCall } from '../../lib/store/crm/types';
import {
  isCallStatusError,
  isCallStatusPending,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import BigThumbsUpIcon from '../../assets/bigImages/BigThumbsUp.png';
import ErrorIcon from '../../assets/img/error-x.png';
import { BaseModal, MobileDrawer, Preloader } from '../../components';
import config from '../../config';
import { useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';
import { PaymentMethodSelectionState } from '../Enrollment/PaymentMethodSelection/types';

import { renderText, renderTitle } from './planDetailsHelpers';

import styles from './AutoPassModal.module.scss';

type Props = {
  isAutoPassPayment: boolean;
  isActivatingAutoPass: boolean;
  toggleModal: () => void;
};

const AutoPassModal = (props: ReduxProps & Props) => {
  const {
    accountId,
    individualId,
    isAutoPassPayment,
    isActivatingAutoPass,
    toggleModal,
    changeAutoPass,
    isUpdateStatusPending,
    isUpdateStatusError,
    tradingPlan,
  } = props;
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [showActivatedAutoPassModal, setShowActivatedAutoPassModal] = useState<boolean>(false);
  const [showFailedAutoPassModal, setShowFailedAutoPassModal] = useState<boolean>(false);
  const [activationRequested, setActivationRequested] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const autoPassDeactivationPrice = tradingPlan?.auto_pass_deactivation_price;
  const isActivationPending = activationRequested && isUpdateStatusPending;

  const handleClose = () => setIsOpen(false);

  const handleActivateAutoPass = () => {
    changeAutoPass(true);
    setActivationRequested(true);
  };

  useEffect(() => {
    if (activationRequested && !isUpdateStatusPending) {
      setShowFailedAutoPassModal(isUpdateStatusError);
      setShowActivatedAutoPassModal(!isUpdateStatusError);
      setActivationRequested(false);
    }
  }, [activationRequested, isUpdateStatusPending, isUpdateStatusError]);

  const handleDeactivateAutoPass = () => {
    sessionStorage.removeItem('paymentGatewayURL');
    if (!individualId || !accountId) {
      return;
    }

    toggleModal();

    const historyStateParams: PaymentMethodSelectionState = {
      accountId,
      individualId,
      purchaseType: PurchasePaymentTypeEnum.AutoPass,
      prevPage: UI_ROUTES.planDetails,
    };

    navigate(
      {
        pathname: UI_ROUTES.paymentMethodSelection,
      },
      { state: historyStateParams },
    );
  };

  const renderActivationButtons = () => {
    if (showFailedAutoPassModal) {
      return (
        <Button biggerFontSize onClick={handleActivateAutoPass}>
          {lang.commonTryAgain()}
        </Button>
      );
    }

    if (showActivatedAutoPassModal) {
      return (
        <Button biggerFontSize onClick={handleClose}>
          {lang.commonOkay()}
        </Button>
      );
    }

    return (
      <>
        <Button biggerFontSize onClick={handleActivateAutoPass}>
          {lang.activateAutoPass()}
        </Button>
        <Button variant={ButtonTypesEnum.Secondary} biggerFontSize onClick={handleClose}>
          {lang.commonCancelButton()}
        </Button>
      </>
    );
  };

  const renderPreloader = () => (
    <div className={styles.autoPassModal}>
      <div className={styles.preloaderWrapper}>
        <Preloader text="" owner="AutoPass activation" />
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={styles.autoPassModal}>
      {!isMobile && showFailedAutoPassModal && (
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close"
          onClick={handleClose}
        />
      )}
      {!isMobile && (
        <>
          {showFailedAutoPassModal ? (
            <img src={ErrorIcon} alt="error" className={styles.errorIcon} />
          ) : (
            <img src={BigThumbsUpIcon} alt="thumbs up" className={styles.thumbsUpIcon} />
          )}
          <div className={styles.title}>
            {renderTitle(
              isAutoPassPayment,
              isActivatingAutoPass,
              showActivatedAutoPassModal,
              showFailedAutoPassModal,
            )}
          </div>
        </>
      )}
      <div className={styles.text}>
        {showFailedAutoPassModal ? (
          <>
            {lang.autoPassActivationFailedText()}
            <a className={styles.link} href={`mailto:${config.links.aceHelpCenterUrl}`}>
              {config.links.aceHelpCenterUrl}
            </a>
          </>
        ) : (
          renderText(isAutoPassPayment, isActivatingAutoPass, showActivatedAutoPassModal)
        )}
      </div>
      <div className={styles.buttonsWrapper}>
        {!isActivatingAutoPass && (
          <>
            {isAutoPassPayment ? (
              <Button
                variant={ButtonTypesEnum.Primary}
                fullWidth
                biggerFontSize
                onClick={handleClose}
              >
                {lang.commonOkay()}
              </Button>
            ) : (
              <>
                <Button fullWidth biggerFontSize onClick={handleClose}>
                  {lang.commonCancelButton()}
                </Button>
                <Button
                  variant={ButtonTypesEnum.Secondary}
                  fullWidth
                  biggerFontSize
                  onClick={handleDeactivateAutoPass}
                >
                  {lang.deactivateAutoPassButton(autoPassDeactivationPrice ?? '')}
                </Button>
              </>
            )}
          </>
        )}
        {isActivatingAutoPass && renderActivationButtons()}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <MobileDrawer
          isOpen={isOpen}
          title={renderTitle(
            isAutoPassPayment,
            isActivatingAutoPass,
            showActivatedAutoPassModal,
            showFailedAutoPassModal,
          )}
          onClose={toggleModal}
        >
          {isActivationPending ? renderPreloader() : renderContent()}
        </MobileDrawer>
      ) : (
        <BaseModal isOpen={isOpen} toggle={toggleModal}>
          {isActivationPending ? renderPreloader() : renderContent()}
        </BaseModal>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: getCurrentDetailedAccount(state)?.account_id,
  individualId: state.crm.individualOrCompanyEmployeeId,
  isUpdateStatusPending: isCallStatusPending(
    CRMCall.clientSettingsTradingPlan,
    state.crm.statusByCall,
  ),
  isUpdateStatusError: isCallStatusError(
    CRMCall.clientSettingsTradingPlan,
    state.crm.statusByCall,
  ),
  tradingPlan: getCurrentAccountTradingPlan(state.crm),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  changeAutoPass: (is_auto_pass_enabled: boolean) =>
    dispatch(updateAutoPass({ is_auto_pass_enabled })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AutoPassModal);
